import React, { createContext, useState, useContext, useCallback } from 'react';
import { userAPI, recruiterAPI } from '../utils/api';

const DataContext = createContext();

export const useData = () => useContext(DataContext);

export const DataProvider = ({ children }) => {
    const [users, setUsers] = useState([]);
    const [recruiters, setRecruiters] = useState([]);
    const [loading, setLoading] = useState({ users: false, recruiters: false });

    const fetchData = useCallback(async (stateKey, forceRefresh = false) => {
        setLoading(prev => ({ ...prev, [stateKey]: true }));
        try {
            let allData = [];
            
            if (stateKey === 'users') {
                // For users, fetch all pages to get complete list
                let page = 1;
                let hasMore = true;
                
                while (hasMore) {
                    const response = await userAPI.getAllUsers(page, 50); // Fetch 50 per page
                    
                    if (response?.data && Array.isArray(response.data)) {
                        allData = [...allData, ...response.data];
                        hasMore = response.pagination?.hasNext || false;
                        page++;
                    } else {
                        hasMore = false;
                    }
                }
                setUsers(allData);
            } else if (stateKey === 'recruiters') {
                // For recruiters, use simple fetch
                const response = await recruiterAPI.getAllRecruiters();
                if (response?.data?.data && Array.isArray(response.data.data)) {
                    allData = response.data.data;
                } 
                else if (response?.data && Array.isArray(response.data)) {
                    allData = response.data;
                } 
                else if (Array.isArray(response)) {
                    allData = response;
                }
                setRecruiters(allData);
            }
        } catch (error) {
            console.error(`Failed to fetch ${stateKey}:`, error);
            if (stateKey === 'users') {
                setUsers([]);
            } else if (stateKey === 'recruiters') {
                setRecruiters([]);
            }
        } finally {
            setLoading(prev => ({ ...prev, [stateKey]: false }));
        }
    }, []);

    const fetchUsers = useCallback((forceRefresh = false) => {
        // Only fetch if no data exists or force refresh is requested
        if (users.length === 0 || forceRefresh) {
            fetchData('users', forceRefresh);
        }
    }, [fetchData, users.length]);

    const fetchRecruiters = useCallback((forceRefresh = false) => {
        // Only fetch if no data exists or force refresh is requested
        if (recruiters.length === 0 || forceRefresh) {
            fetchData('recruiters', forceRefresh);
        }
    }, [fetchData, recruiters.length]);

    const value = {
        users,
        recruiters,
        fetchUsers,
        fetchRecruiters,
        loading,
        refreshUsers: () => fetchUsers(true),
        refreshRecruiters: () => fetchRecruiters(true),
    };

    return (
        <DataContext.Provider value={value}>
            {children}
        </DataContext.Provider>
    );
}; 